/**
 * One scroll reading, shared by the motion layer and the scene.
 *
 * The machine's choreography and the pin that keeps it on screen have to agree
 * about how far through the opening the reader is, or the machine performs
 * after it has scrolled away — which is what it did before this existed.
 *
 * `motion.ts` owns the pin and writes here. `scene.ts` reads. If the motion
 * bundle never runs — reduced motion, a failed dynamic import, no GSAP — then
 * `driven` stays false and the scene falls back to measuring scroll itself, so
 * the machine still moves on a page with no motion layer at all.
 */

export interface HeroScroll {
  /** 0 at the top of the opening, 1 at the end of the pinned run. */
  progress: number
  /** Signed pixels per frame, smoothed. Drives bubble emission and wand speed. */
  velocity: number
  /** True once the motion layer has taken over. */
  driven: boolean
  /** True once the machine has shrunk to its perch beside the timeline. The
   *  scene reads it to cut the bubble rate and reframe, because a machine two
   *  inches tall on a phone does not need thirty-eight sprites. */
  docked: boolean
  /** 0 at the first step, 1 at the last. Normalised over the timeline element,
   *  so adding or deleting a card changes what each step maps to and nothing
   *  has to be re-rendered or re-tuned. */
  steps: number
}

const state: HeroScroll = { progress: 0, velocity: 0, driven: false, docked: false, steps: 0 }

export function readHeroScroll(): Readonly<HeroScroll> {
  return state
}

export function driveHeroScroll(progress: number, velocity: number): void {
  state.progress = Math.min(1, Math.max(0, progress))
  state.velocity = velocity
  state.driven = true
}

export function releaseHeroScroll(): void {
  state.driven = false
  state.docked = false
}

export function setHeroDocked(docked: boolean): void {
  state.docked = docked
}

export function driveSteps(progress: number): void {
  state.steps = Math.min(1, Math.max(0, progress))
}
